/**
 * Theme Configuration
 * All color schemes, theme variants, and UI styling configuration
 */

// Primary Color (default - will be overridden by store config)
type HexColor = string;
export const primaryColor: HexColor = '#800080';

// State Colors
export const stateErrorColor = '#C41E3A';
export const stateSuccessColor = '#228B22';
export const stateWarningColor = '#FFA500';
export const stateInfoColor = '#0096FF';
export const stateDisabledColor = '#D3D3D3';

// Theme Background Colors
export const lightBackground = '#FFFFFF';
export const lightBackgroundSecondary = '#F9F9F9';
export const lightBackgroundTertiary = '#FAFAFA';

export const darkBackground = '#121212';
export const darkBackgroundSecondary = '#1E1E1E';
export const darkBackgroundTertiary = '#333333';

// Text Colors
export const lightTextPrimary = '#000000';
export const lightTextSecondary = '#666666';
export const lightTextMuted = '#999999';

export const darkTextPrimary = '#FFFFFF';
export const darkTextSecondary = '#CCCCCC';
export const darkTextMuted = '#999999';

// Theme Colors Configuration
export const themeColors = {
    light: {
        // Backgrounds
        background: lightBackgroundTertiary,
        backgroundSecondary: lightBackgroundSecondary,
        backgroundTertiary: lightBackgroundTertiary,
        
        // Text
        textPrimary: lightTextPrimary,
        textSecondary: lightTextSecondary,
        textMuted: lightTextMuted,
        
        // UI Elements
        border: '#E0E0E0',
        divider: '#EEEEEE',
        hover: '#F5F5F5',
        buttonOutline: primaryColor,
        
        // Functional
        cardBackground: lightBackground,
        navBackground: lightBackground,
        inputBackground: lightBackground,
    },
    dark: {
        // Backgrounds
        background: darkBackground,
        backgroundSecondary: darkBackgroundSecondary,
        backgroundTertiary: darkBackgroundTertiary,
        
        // Text
        textPrimary: darkTextPrimary,
        textSecondary: darkTextSecondary,
        textMuted: darkTextMuted,
        
        // UI Elements
        border: '#444444',
        divider: '#333333',
        hover: '#333333',
        buttonOutline: '#FFFFFF',
        
        // Functional
        cardBackground: darkBackgroundSecondary,
        navBackground: darkBackground,
        inputBackground: darkBackgroundTertiary,
    }
};

// Activity Type Colors (for analytics) - Hex colors
export const ActivityTypeColors = {
    page_view: '#2196F3',           // Blue
    product_view: '#9C27B0',        // Purple
    navigation: '#607D8B',          // Blue Grey
    category_filtering: '#FF9800',  // Orange
    cart_add: '#4CAF50',           // Green
    checkout_start: '#FF9800',      // Orange
    checkout_complete: '#4CAF50',   // Green
    sign_up: '#4CAF50',            // Green
    sign_in: '#2196F3',            // Blue
    sign_out: '#C41E3A',           // Dark Red
} as const;

export type ActivityTypeColorKey = keyof typeof ActivityTypeColors;
export type ActivityTypeColorValue = typeof ActivityTypeColors[ActivityTypeColorKey];

// Portal UI Styling - Global configuration
// Simple two-mode system: shadow (true) or border (false)
export const PortalUIConfig = {
    // Cards - show shadow or border
    card: {
        shadow: true,  // true = shadow, false = border only
    },
    
    // Tables - show shadow or border
    table: {
        shadow: true,  // true = shadow, false = border only
    },
    
    // Toolbars - show shadow or border (toolbars use subtle backgrounds)
    toolbar: {
        shadow: true,  // true = shadow, false = border only
    },
} as const;

/**
 * Portal Component Style Utilities
 * Simple two-mode system: shadow OR border
 */

/**
 * Get styles for Portal components (cards, tables, toolbars)
 * @param theme - MUI theme object
 * @param useShadow - true = shadow, false = border only
 * @param baseStyles - Additional base styles to merge
 * @param type - Component type ('card' | 'toolbar') - affects background handling
 */
export const getPortalStyles = (
    theme: any,
    useShadow: boolean,
    baseStyles: any = {},
    type: 'card' | 'toolbar' = 'card'
) => {
    const base = {
        borderRadius: 2,
    };

    if (useShadow) {
        // Shadow mode: elevated appearance
        return {
            ...base,
            backgroundColor: theme.palette.background.paper,
            boxShadow: type === 'toolbar' ? theme.shadows[1] : theme.shadows[2],
            border: 'none',
            ...baseStyles
        };
    } else {
        // Border mode: flat appearance with border
        return {
            ...base,
            backgroundColor: type === 'toolbar' 
                ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)')
                : theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
            ...baseStyles
        };
    }
};

// Helper functions for each component type
export const getCardStyles = (theme: any, baseStyles: any = {}) =>
    getPortalStyles(theme, PortalUIConfig.card.shadow, baseStyles, 'card');

export const getTableStyles = (theme: any, baseStyles: any = {}) =>
    getPortalStyles(theme, PortalUIConfig.table.shadow, baseStyles, 'card');

export const getToolbarStyles = (theme: any, baseStyles: any = {}) =>
    getPortalStyles(theme, PortalUIConfig.toolbar.shadow, baseStyles, 'toolbar');

